import { Box, Tooltip, Typography } from "@mui/material";
import { format } from "date-fns";
import { useCandidateRejectionReasons } from "../../../../../common/components/functional/candidateRejection/useCandidateRejectionReasons";
import { useTranslation } from "react-i18next";
import { ReactComponent as Rejected } from "../../../../../common/icons/delete-filled.svg";
import { ReactComponent as IncompletedStatusIndicator } from "../../../../../common/icons/empty-ball.svg";
import type { CandidateRejectionReason } from "../../../../../common/types/CandidateRejectionReason";
import { Connector, StatusIndicator } from "../styles";

type Props = {
  currentStatusCompleted: boolean;
  nextStatusCompleted: boolean;
  noStatuses: boolean;
  success?: boolean;
  isLast: boolean;
  color: string;
  at?: number;
  by?: string;
  rejectionDetailsVisible?: boolean;
  rejectionReason?: CandidateRejectionReason | null;
  rejectionComment?: string | null;
};

const TalentStatusIndicator = ({
  currentStatusCompleted,
  nextStatusCompleted,
  noStatuses,
  success,
  isLast,
  color,
  at,
  by,
  rejectionDetailsVisible,
  rejectionReason,
  rejectionComment,
}: Props) => {
  const { t } = useTranslation("order");
  const labels = useCandidateRejectionReasons();
  const showRejectionDetails = rejectionDetailsVisible ?? success === false;
  const completedConnector =
    nextStatusCompleted && !isLast && currentStatusCompleted;

  const tooltipTitle = currentStatusCompleted ? (
    <>
      <Typography fontSize="11px" fontWeight="600">
        {at && format(new Date(at), "dd.MM.yyyy")}
      </Typography>
      <Typography fontSize="11px" fontWeight="600">
        {by && `By ${by}`}
      </Typography>
      {showRejectionDetails && rejectionReason && (
        <Typography fontSize="11px" fontWeight="600">
          {t("common:rejectionReason")}: {labels[rejectionReason]}
        </Typography>
      )}
      {showRejectionDetails && rejectionComment && (
        <Typography
          fontSize="11px"
          sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
        >
          {rejectionComment}
        </Typography>
      )}
    </>
  ) : null;

  const renderIndicator = () => {
    // workAgreementAccepted status has no success field?
    if (currentStatusCompleted && (success || success === undefined)) {
      return <StatusIndicator sx={{ bgcolor: color, zIndex: 2 }} />;
    }

    if (success === false) {
      return <Rejected style={{ zIndex: 2 }} />;
    }

    return <IncompletedStatusIndicator style={{ zIndex: 2 }} />;
  };

  return (
    <Tooltip title={tooltipTitle} placement="top" arrow>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        maxHeight="20px"
        position="relative"
      >
        {!noStatuses && (
          <Connector
            sx={{
              display: isLast ? "none" : "block",
              bgcolor: completedConnector ? "primary.main" : "#000A441C",
            }}
          />
        )}
        {renderIndicator()}
      </Box>
    </Tooltip>
  );
};

export default TalentStatusIndicator;
